import type { Earning, LatestEarnings } from "@/domain/gems-game/domain";
import type { Quest, QuestGroupState } from "@ui-kit/QuestList";
import type { QuestGroup, QuestStatus } from "@ui-kit/QuestList";

const getLockedState = (
  quest: Quest,
  status: QuestStatus,
  lockedUntil?: Date,
) => {
  if (quest.waitTimeInHrs && !lockedUntil) {
    return true;
  }

  return status === "pending" && lockedUntil && lockedUntil >= new Date();
};

const getQuestLockedDate = (
  quest: Quest,
  prevEarning?: Earning,
): Date | undefined => {
  const waitTime =
    quest?.waitTimeInHrs && !Number.isNaN(quest.waitTimeInHrs)
      ? quest.waitTimeInHrs * 60 * 60 * 1000
      : undefined;

  if (!waitTime || !prevEarning?.timestamp) {
    return undefined;
  }

  try {
    const prevTimestamp = new Date(prevEarning.timestamp).getTime();
    return new Date(prevTimestamp + waitTime);
  } catch {
    return undefined;
  }
};

const getQuestLockedUntil = (
  completed?: boolean,
  earnings?: Earning,
  quest?: Quest,
) => {
  if (completed && earnings && quest) {
    return getQuestLockedDate(quest, earnings);
  }
  return undefined;
};

const getQuestStatus = (completed?: boolean) =>
  completed ? "completed" : "pending";

const findEarningByRuleId = (
  ruleId: string,
  earnings?: LatestEarnings,
  excludedRuleIds = ["daily-gems-claim"],
) => {
  const filteredEarnings = earnings?.earnings.filter(
    (e) => !excludedRuleIds.includes(e.ruleId),
  );

  const earning = filteredEarnings?.find((e) => e.ruleId === ruleId);
  return earning;
};

export const computeQuestStatusFields = (
  questGroups: QuestGroup[] | QuestGroupState[],
  currentGroup: QuestGroup,
  groupIndex: number,
  currentQuest: Quest,
  questIndex: number,
  latestEarnings?: LatestEarnings,
) => {
  const previousQuest =
    questIndex > 0
      ? currentGroup.quests[questIndex - 1]
      : groupIndex > 0
        ? questGroups[groupIndex - 1].quests[
            questGroups[groupIndex - 1].quests.length - 1
          ]
        : undefined;

  const currentEarning = findEarningByRuleId(
    currentQuest.ruleId,
    latestEarnings,
  );

  const currentQuestIsCompleted = Boolean(currentEarning);

  const previousEarning = previousQuest
    ? findEarningByRuleId(previousQuest.ruleId, latestEarnings)
    : undefined;
  const previousQuestCompleted = Boolean(previousEarning);

  const status = getQuestStatus(currentQuestIsCompleted);

  const lockedUntil = getQuestLockedUntil(
    previousQuestCompleted,
    previousEarning,
    currentQuest,
  );

  const locked = getLockedState(currentQuest, status, lockedUntil) || false;

  const completedAt = currentEarning?.timestamp
    ? new Date(currentEarning.timestamp)
    : undefined;
  const completed = status === "completed";
  const hidden = status === "completed";
  const completionCount =
    currentEarning?.total &&
    currentEarning?.increase &&
    currentEarning.increase > 0
      ? Math.floor(currentEarning.total / currentEarning.increase)
      : 0;

  const icon = locked ? "Question" : currentQuest.icon;
  const baseIcon =
    "baseIcon" in currentQuest ? currentQuest.baseIcon : currentQuest.icon;
  const skippable =
    questGroups.length - 1 === groupIndex ? false : currentQuest?.skippable;

  return {
    status,
    locked,
    lockedUntil,
    completedAt,
    completed,
    hidden,
    completionCount,
    icon,
    skippable,
    baseIcon,
  };
};
